import { X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { cx } from '../utils/cx';
import { ToneIcon } from './Badge';
import { IconButton } from './IconButton';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface ToastInput {
  tone?: ToastTone;
  title: string;
  description?: string;
  /** Durée d'affichage ; 0 = reste affiché jusqu'à fermeture. Erreurs : persistantes par défaut. */
  durationMs?: number;
}

interface ToastItem extends Required<Pick<ToastInput, 'tone' | 'title'>> {
  id: number;
  description?: string | undefined;
  durationMs: number;
}

interface ToastApi {
  show: (toast: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({
  children,
  label = 'Notifications',
}: {
  children: ReactNode;
  label?: string;
}) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback((input: ToastInput) => {
    const id = nextId.current++;
    const tone = input.tone ?? 'info';
    const durationMs = input.durationMs ?? (tone === 'danger' ? 0 : 5000);
    setToasts((current) => [
      ...current,
      { id, tone, title: input.title, description: input.description, durationMs },
    ]);
    return id;
  }, []);

  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);
  const polite = toasts.filter((toast) => toast.tone !== 'danger');
  const assertive = toasts.filter((toast) => toast.tone === 'danger');

  return (
    <ToastContext value={api}>
      {children}
      {/* Les régions live existent avant l'arrivée des messages pour être annoncées. */}
      <section className="av-toast-region" aria-label={label}>
        <div role="status" aria-live="polite">
          {polite.map((toast) => (
            <ToastView key={toast.id} toast={toast} onDismiss={dismiss} />
          ))}
        </div>
        <div role="alert" aria-live="assertive">
          {assertive.map((toast) => (
            <ToastView key={toast.id} toast={toast} onDismiss={dismiss} />
          ))}
        </div>
      </section>
    </ToastContext>
  );
}

function ToastView({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  useEffect(() => {
    if (toast.durationMs <= 0) return;
    const timer = setTimeout(() => {
      onDismiss(toast.id);
    }, toast.durationMs);
    return () => {
      clearTimeout(timer);
    };
  }, [toast.id, toast.durationMs, onDismiss]);

  return (
    <div className={cx('av-toast', `av-toast--${toast.tone}`)}>
      <ToneIcon tone={toast.tone} className="av-toast__icon" />
      <div>
        <p className="av-toast__title">{toast.title}</p>
        {toast.description ? <p className="av-toast__description">{toast.description}</p> : null}
      </div>
      <IconButton
        label="Fermer la notification"
        onClick={() => {
          onDismiss(toast.id);
        }}
      >
        <X />
      </IconButton>
    </div>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast doit être utilisé dans <ToastProvider>.');
  return api;
}
